import { c as _c } from "react/compiler-runtime";
import { FileSearch } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { BagIcon, ChevronDown16Icon, HeartIcon, ProfileIcon } from "@/components/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DepartmentMega } from "@/components/layout/department-mega";
import { HeaderChatLink } from "../chat/header-chat-link";
import type { Department } from "@/lib/home-feed";
import { useCategoryTree } from "@/lib/use-category-tree";
import { cn } from "@/lib/utils";
import { useCart, useDepartment, usePreferences, useProfile, useWishlist } from "@/store";
import { LocationButton } from "@/components/location-modal";
import { Logo } from "./logo";
import { SearchBar } from "./search-bar";

// Ширины: десктопная шапка (кадры 1200) — от 1024px (lg); ниже — планшетная схема
// из кадра «Tablet» 744 (профиль + поиск) и нижний таб-бар MobileTabBar; отдельного
// мобильного кадра шапки нет, поэтому < 744 используется та же схема. Раздел
// «Уведомления»/«Поддержка» доступен через профиль (/profile/*).

// Department tabs («Одежда» / «Дом»): Manrope 16/20 w600 ls −0,31. В макете
// (2871:30392) все пункты одного цвета — #1F1F1F на белом, #FFF 96 % над hero;
// активный раздел отмечен подчёркиванием (Айталы A-02), а не приглушением
// остальных. Каждый пункт — «таблетка» 40px (r100, pad 6), между пунктами 10px,
// как в кадре «Поиск»; пункт, чьё мега-меню открыто (`highlighted`), — tangelo
// (кадры «Ховер»; поведение меню не меняем).
// Shared by the desktop header and the mobile menu — drives the persisted
// department scope in preferences.
//
// Разделов может быть больше, чем помещается (QA 20.10 №16/17: при 8 разделах
// поиск, город, избранное, корзина и профиль уезжали за правый край на 1280 и
// 1440). Полоса разделов сжимается в отведённое ей место: что не влезло, уходит
// в «Ещё ⌄» (список по нажатию). Активный раздел всегда остаётся на виду. Пока
// всё помещается (сейчас на проде 3 раздела), «Ещё» нет и шапка как раньше.
const TAB_GAP = 10; // gap-2.5

function tabClass({
  size,
  active,
  highlighted,
  onDark
}: {
  size: "md" | "lg";
  active: boolean;
  highlighted: boolean;
  onDark: boolean;
}) {
  return cn("flex h-10 shrink-0 items-center whitespace-nowrap rounded-full px-1.5 text-[16px] font-semibold leading-5 tracking-[-0.31px] outline-none transition-colors focus-visible:text-osmix-tangelo", size === "lg" && "text-lg",
  // Подчёркивание активного: 1,5px, ~9px под базовой линией — как
  // отрисовано в кадре 2871:30392 (линия на всю ширину слова).
  active && "underline decoration-[1.5px] underline-offset-[7px]", highlighted ? "text-osmix-tangelo" : onDark ? "text-white/96" : "text-[#1F1F1F]");
}

// Какие разделы показать в полосе шириной `avail`: все, если влезают; иначе
// столько первых, сколько помещается вместе с «Ещё», плюс активный.
function fitTabs(widths: number[], moreWidth: number, avail: number, activeIndex: number): number[] {
  const all = widths.map((_, i) => i);
  const total = widths.reduce((sum, w) => sum + w, 0) + TAB_GAP * Math.max(0, widths.length - 1);
  if (total <= avail + 1) return all;
  let used = moreWidth;
  if (activeIndex >= 0) used += (widths[activeIndex] ?? 0) + TAB_GAP;
  const shown = new Set<number>(activeIndex >= 0 ? [activeIndex] : []);
  for (const i of all) {
    if (i === activeIndex) continue;
    const w = (widths[i] ?? 0) + TAB_GAP;
    if (used + w > avail + 1) break;
    used += w;
    shown.add(i);
  }
  return all.filter(i => shown.has(i));
}
function DepartmentTabs(t0) {
  const $ = _c(83);
  const {
    className,
    size: t1,
    onDark: t2,
    onHover,
    onHoverNone,
    highlighted: t3
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const onDark = t2 === undefined ? false : t2;
  const highlighted = t3 === undefined ? null : t3;
  const {
    t
  } = useTranslation();
  const department = useDepartment();
  const setDepartment = usePreferences(_temp);
  const {
    departments
  } = useCategoryTree();
  let t4;
  if ($[0] !== department || $[1] !== departments) {
    t4 = departments.some(d => d.key === department) ? department : "clothes";
    $[0] = department;
    $[1] = departments;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const activeKey = t4;
  let t5;
  if ($[3] !== activeKey || $[4] !== departments) {
    let t6;
    if ($[6] !== activeKey) {
      t6 = d_0 => d_0.key === activeKey;
      $[6] = activeKey;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    t5 = departments.findIndex(t6);
    $[3] = activeKey;
    $[4] = departments;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const activeIndex = t5;
  const boxRef = useRef(null);
  const measureRef = useRef(null);
  const [shown, setShown] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const count = departments.length;
  let t6;
  let t7;
  if ($[8] !== activeIndex || $[9] !== count) {
    t6 = () => {
      const box = boxRef.current;
      const measure = measureRef.current;
      if (!box || !measure) {
        return;
      }
      const recompute = () => {
        const items = (Array.from(measure.children) as HTMLElement[]).slice(0, count);
        const more = measure.children[count] as HTMLElement | undefined;
        const widths = items.map(_temp2);
        const next = fitTabs(widths, (more?.getBoundingClientRect().width ?? 0) + TAB_GAP, box.getBoundingClientRect().width, activeIndex);
        setShown(prev => prev && prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next);
      };
      recompute();
      const ro = new ResizeObserver(recompute);
      ro.observe(box);
      ro.observe(measure);
      return () => ro.disconnect();
    };
    t7 = [count, activeIndex];
    $[8] = activeIndex;
    $[9] = count;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[12] !== departments || $[13] !== shown) {
    t8 = shown ? departments.filter((_, i_0) => shown.includes(i_0)) : departments;
    $[12] = departments;
    $[13] = shown;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const visible = t8;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t9;
  if ($[15] !== activeKey || $[16] !== className || $[17] !== departments || $[18] !== highlighted || $[19] !== moreOpen || $[20] !== onDark || $[21] !== onHover || $[22] !== onHoverNone || $[23] !== setDepartment || $[24] !== shown || $[25] !== size || $[26] !== t || $[27] !== visible) {
    const hidden = shown ? departments.filter((__0, i_1) => !shown.includes(i_1)) : [];
    t12 = boxRef;
    if ($[34] !== className) {
      t13 = cn("min-w-0 overflow-hidden", className);
      $[34] = className;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t15;
    if ($[36] !== activeKey || $[37] !== departments || $[38] !== onDark || $[39] !== size) {
      let t16;
      if ($[41] !== activeKey || $[42] !== onDark || $[43] !== size) {
        t16 = d_1 => <span key={d_1.key} className={tabClass({
          size,
          active: d_1.key === activeKey,
          highlighted: false,
          onDark
        })}>{d_1.label}</span>;
        $[41] = activeKey;
        $[42] = onDark;
        $[43] = size;
        $[44] = t16;
      } else {
        t16 = $[44];
      }
      t15 = departments.map(t16);
      $[36] = activeKey;
      $[37] = departments;
      $[38] = onDark;
      $[39] = size;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    let t16;
    if ($[45] !== onDark || $[46] !== size) {
      t16 = cn(tabClass({
        size,
        active: false,
        highlighted: false,
        onDark
      }), "absolute left-0 top-0 gap-1");
      $[45] = onDark;
      $[46] = size;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    let t17;
    if ($[48] !== t) {
      t17 = t("header.moreDepartments", {
        defaultValue: "\u0415\u0449\u0451"
      });
      $[48] = t;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    let t18;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <ChevronDown16Icon />;
      $[50] = t18;
    } else {
      t18 = $[50];
    }
    let t19;
    if ($[51] !== t16 || $[52] !== t17) {
      t19 = <span className={t16}>{t17}{t18}</span>;
      $[51] = t16;
      $[52] = t17;
      $[53] = t19;
    } else {
      t19 = $[53];
    }
    if ($[54] !== t15 || $[55] !== t19) {
      t14 = <div aria-hidden="true" className="invisible h-0 overflow-hidden"><div ref={measureRef} className="relative flex w-max items-center gap-2.5">{t15}{t19}</div></div>;
      $[54] = t15;
      $[55] = t19;
      $[56] = t14;
    } else {
      t14 = $[56];
    }
    t9 = "flex items-center gap-2.5";
    let t20;
    if ($[57] !== activeKey || $[58] !== highlighted || $[59] !== onDark || $[60] !== onHover || $[61] !== setDepartment || $[62] !== size || $[63] !== visible) {
      let t21;
      if ($[65] !== activeKey || $[66] !== highlighted || $[67] !== onDark || $[68] !== onHover || $[69] !== setDepartment || $[70] !== size) {
        t21 = d_2 => {
          const active = d_2.key === activeKey;
          return <button key={d_2.key} type="button" role="tab" aria-selected={active} onMouseEnter={() => onHover?.(d_2.key)} onFocus={() => onHover?.(d_2.key)} onClick={() => setDepartment(d_2.key)} className={tabClass({
            size,
            active,
            highlighted: highlighted === d_2.key,
            onDark
          })}>{d_2.label}</button>;
        };
        $[65] = activeKey;
        $[66] = highlighted;
        $[67] = onDark;
        $[68] = onHover;
        $[69] = setDepartment;
        $[70] = size;
        $[71] = t21;
      } else {
        t21 = $[71];
      }
      t20 = visible.map(t21);
      $[57] = activeKey;
      $[58] = highlighted;
      $[59] = onDark;
      $[60] = onHover;
      $[61] = setDepartment;
      $[62] = size;
      $[63] = visible;
      $[64] = t20;
    } else {
      t20 = $[64];
    }
    if ($[72] !== t20) {
      t10 = <div className="flex items-center gap-2.5" role="tablist" aria-label={"\u0420\u0430\u0437\u0434\u0435\u043B"}>{t20}</div>;
      $[72] = t20;
      $[73] = t10;
    } else {
      t10 = $[73];
    }
    t11 = hidden.length > 0 && <Popover open={moreOpen} onOpenChange={setMoreOpen}><PopoverTrigger asChild={true}><button type="button" aria-label={t("header.moreDepartmentsLabel", {
          defaultValue: "\u0415\u0449\u0451 \u0440\u0430\u0437\u0434\u0435\u043B\u044B"
        })} onMouseEnter={onHoverNone} onFocus={onHoverNone} className={cn(tabClass({
          size,
          active: false,
          highlighted: moreOpen,
          onDark
        }), "gap-1")}>{t("header.moreDepartments", {
            defaultValue: "\u0415\u0449\u0451"
          })}<ChevronDown16Icon className={cn("transition-transform", moreOpen && "rotate-180")} /></button></PopoverTrigger><PopoverContent align="start" className="w-auto min-w-[200px] rounded-[10px] border-0 bg-white p-[3px] text-ds-primary shadow-[var(--ds-shadow-dropdown)]"><div className="flex flex-col" role="menu">{hidden.map(d_3 => <button key={d_3.key} type="button" role="menuitem" onClick={() => {
            setDepartment(d_3.key);
            setMoreOpen(false);
          }} className="flex w-full items-center rounded-[8px] px-3 py-2.5 text-left text-[14px] font-medium leading-[19px] text-ds-primary outline-none hover:bg-ds-hover focus-visible:bg-ds-hover">{d_3.label}</button>)}</div></PopoverContent></Popover>;
    $[15] = activeKey;
    $[16] = className;
    $[17] = departments;
    $[18] = highlighted;
    $[19] = moreOpen;
    $[20] = onDark;
    $[21] = onHover;
    $[22] = onHoverNone;
    $[23] = setDepartment;
    $[24] = shown;
    $[25] = size;
    $[26] = t;
    $[27] = visible;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t9;
  } else {
    t10 = $[28];
    t11 = $[29];
    t12 = $[30];
    t13 = $[31];
    t14 = $[32];
    t9 = $[33];
  }
  let t15;
  if ($[74] !== t10 || $[75] !== t11 || $[76] !== t9) {
    t15 = <div className={t9}>{t10}{t11}</div>;
    $[74] = t10;
    $[75] = t11;
    $[76] = t9;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  let t16;
  if ($[78] !== t12 || $[79] !== t13 || $[80] !== t14 || $[81] !== t15) {
    t16 = <div ref={t12} className={t13}>{t14}{t15}</div>;
    $[78] = t12;
    $[79] = t13;
    $[80] = t14;
    $[81] = t15;
    $[82] = t16;
  } else {
    t16 = $[82];
  }
  return t16;
}

// Round icon button with an optional count bubble (heart / bag / user).
// Кадр «Поиск»: кнопка 40×40 r100 (pad 6), иконка 24×24 — размер задаём здесь
// (`[&>svg]:size-6`), чтобы все иконки ряда были одинаковыми.
function _temp2(el) {
  return el.getBoundingClientRect().width;
}
function _temp(s) {
  return s.setDepartment;
}
function IconLink(t0) {
  const $ = _c(10);
  const {
    to,
    label,
    count,
    onDark: t1,
    children
  } = t0;
  const onDark = t1 === undefined ? false : t1;
  const t2 = count > 0 ? `${label} (${count})` : label;
  const t3 = onDark ? "text-white hover:bg-white/15" : "text-osmix-text-1 hover:bg-osmix-gray-100";
  let t4;
  if ($[0] !== t3) {
    t4 = cn("relative flex size-10 items-center justify-center rounded-full transition-colors [&>svg]:size-6", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== count) {
    t5 = count > 0 && <span className="absolute -right-0.5 -top-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-osmix-tangelo px-1 text-[11px] font-bold leading-[18px] text-white">{count > 99 ? "99+" : count}</span>;
    $[2] = count;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== children || $[5] !== t2 || $[6] !== t4 || $[7] !== t5 || $[8] !== to) {
    t6 = <Link to={to} aria-label={t2} className={t4}>{children}{t5}</Link>;
    $[4] = children;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
    $[8] = to;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

// `overlay` — шапка ложится ПОВЕРХ тёмного hero (главная): пока под ней hero,
// фон прозрачный, текст/иконки белые (как в Figma). На любой странице над
// первым экраном шапка без подложки; когда под неё уезжает контент — эффект
// стекла (полупрозрачность + размытие), а не белая плашка. Отрицательный нижний
// отступ (по замеренной высоте) подтягивает hero под шапку.
export function SiteHeader(t0) {
  const $ = _c(90);
  const {
    overlay: t1,
    lightLogo: t2
  } = t0;
  const overlay = t1 === undefined ? false : t1;
  const lightLogo = t2 === undefined ? false : t2;
  const {
    t
  } = useTranslation();
  const cartCount = useCart(_temp3);
  const orderCount = useProfile(_temp4);
  const wishCount = useWishlist(_temp5);
  const [hoveredDept, setHoveredDept] = useState(null);
  const closeTimer = useRef(undefined);
  const {
    categories
  } = useCategoryTree();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = d => {
      window.clearTimeout(closeTimer.current);
      setHoveredDept(d);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const openMega = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      window.clearTimeout(closeTimer.current);
      setHoveredDept(null);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const closeMegaNow = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      closeTimer.current = window.setTimeout(() => setHoveredDept(null), 120);
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const closeMegaSoon = t5;
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  const [overHero, setOverHero] = useState(overlay);
  const [scrolled, setScrolled] = useState(false);
  let t6;
  let t7;
  if ($[3] !== overlay) {
    t6 = () => {
      const el = ref.current;
      const measure = () => setHeight(el?.offsetHeight ?? 0);
      measure();
      const ro = new ResizeObserver(measure);
      if (el) {
        ro.observe(el);
      }
      const onScroll = () => {
        const headerH = el?.offsetHeight ?? 0;
        const hero = overlay ? document.querySelector("[data-header-hero]") : null;
        const heroBottom = hero ? hero.getBoundingClientRect().bottom : 0;
        const onHero = hero != null && heroBottom > headerH;
        setOverHero(onHero);
        setScrolled(!onHero && window.scrollY > 4);
      };
      onScroll();
      let raf = 0;
      const mo = overlay ? new MutationObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(onScroll);
      }) : null;
      mo?.observe(document.body, {
        childList: true,
        subtree: true
      });
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      window.addEventListener("resize", onScroll);
      return () => {
        ro.disconnect();
        mo?.disconnect();
        cancelAnimationFrame(raf);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      };
    };
    t7 = [overlay];
    $[3] = overlay;
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  useEffect(t6, t7);
  const onDark = overlay && overHero && !hoveredDept;
  let t8;
  if ($[6] !== height || $[7] !== overlay) {
    t8 = overlay ? {
      marginBottom: -height
    } : undefined;
    $[6] = height;
    $[7] = overlay;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = hoveredDept ? "bg-white" : scrolled ? "bg-white/90 shadow-[inset_0_-1px_0_rgba(255,255,255,0.55),0_1px_16px_rgba(12,12,13,0.06)] backdrop-blur-[18px] backdrop-saturate-[1.8] supports-[backdrop-filter]:bg-white/50 [@media(prefers-reduced-transparency:reduce)]:bg-white" : "bg-transparent";
  let t10;
  if ($[9] !== t9) {
    t10 = cn("sticky top-0 z-40 transition-[background-color,box-shadow,backdrop-filter] duration-300", t9);
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t) {
    t11 = t("nav.profile");
    $[11] = t;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ProfileIcon size={24} />;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== onDark || $[15] !== orderCount || $[16] !== t11) {
    t13 = <IconLink to="/profile" label={t11} count={orderCount} onDark={onDark}>{t12}</IconLink>;
    $[14] = onDark;
    $[15] = orderCount;
    $[16] = t11;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  let t15;
  let t16;
  if ($[18] !== onDark) {
    t14 = <SearchBar className="min-w-0 flex-1 min-[744px]:absolute min-[744px]:left-1/2 min-[744px]:w-[calc(100%-160px)] min-[744px]:max-w-[344px] min-[744px]:flex-none min-[744px]:-translate-x-1/2" onDark={onDark} />;
    t15 = <LocationButton className="ml-auto hidden min-[744px]:inline-flex" onDark={onDark} media="(min-width: 744px) and (max-width: 1023.98px)" />;
    t16 = <LocationButton variant="icon" className="min-[744px]:hidden" onDark={onDark} media="(max-width: 743.98px)" />;
    $[18] = onDark;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
  } else {
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t13 || $[23] !== t14 || $[24] !== t15 || $[25] !== t16) {
    t17 = <div className="relative flex h-10 items-center gap-2 lg:hidden">{t13}{t14}{t15}{t16}</div>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = lightLogo && onDark ? "light" : "brand";
  let t19;
  if ($[27] !== t18) {
    t19 = <Logo className="shrink-0" tone={t18} />;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== hoveredDept || $[30] !== onDark) {
    t20 = <div className="min-w-0" onMouseLeave={closeMegaSoon}><DepartmentTabs onDark={onDark} onHover={openMega} onHoverNone={closeMegaNow} highlighted={hoveredDept} /></div>;
    $[29] = hoveredDept;
    $[30] = onDark;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t19 || $[33] !== t20) {
    t21 = <div className="flex min-w-0 items-center gap-2.5">{t19}{t20}</div>;
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== onDark) {
    t22 = <SearchBar className="mx-auto w-full min-w-[200px] max-w-[522px] shrink-[10000]" onDark={onDark} />;
    $[35] = onDark;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== onDark) {
    t23 = <LocationButton className="mr-1.5" onDark={onDark} media="(min-width: 1024px)" />;
    $[37] = onDark;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== t) {
    t24 = t("nav.wishlist");
    $[39] = t;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <HeartIcon size={24} />;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== onDark || $[43] !== t24 || $[44] !== wishCount) {
    t26 = <IconLink to="/wishlist" label={t24} count={wishCount} onDark={onDark}>{t25}</IconLink>;
    $[42] = onDark;
    $[43] = t24;
    $[44] = wishCount;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t) {
    t27 = t("nav.cart");
    $[46] = t;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <BagIcon size={24} />;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== cartCount || $[50] !== onDark || $[51] !== t27) {
    t29 = <IconLink to="/cart" label={t27} count={cartCount} onDark={onDark}>{t28}</IconLink>;
    $[49] = cartCount;
    $[50] = onDark;
    $[51] = t27;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== onDark) {
    t30 = <HeaderChatLink onDark={onDark} />;
    $[53] = onDark;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== t) {
    t31 = t("nav.requests");
    $[55] = t;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <FileSearch size={24} strokeWidth={1.5} aria-hidden="true" />;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== onDark || $[59] !== t31) {
    t33 = <IconLink to="/requests" label={t31} count={0} onDark={onDark}>{t32}</IconLink>;
    $[58] = onDark;
    $[59] = t31;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== t) {
    t34 = t("nav.profile");
    $[61] = t;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <ProfileIcon size={24} />;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== onDark || $[65] !== orderCount || $[66] !== t34) {
    t36 = <IconLink to="/profile" label={t34} count={orderCount} onDark={onDark}>{t35}</IconLink>;
    $[64] = onDark;
    $[65] = orderCount;
    $[66] = t34;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] !== t23 || $[69] !== t26 || $[70] !== t29 || $[71] !== t30 || $[72] !== t33 || $[73] !== t36) {
    t37 = <div className="flex shrink-0 items-center gap-2">{t23}{t26}{t29}{t30}{t33}{t36}</div>;
    $[68] = t23;
    $[69] = t26;
    $[70] = t29;
    $[71] = t30;
    $[72] = t33;
    $[73] = t36;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== t21 || $[76] !== t22 || $[77] !== t37) {
    t38 = <div className="hidden items-center gap-8 lg:flex">{t21}{t22}{t37}</div>;
    $[75] = t21;
    $[76] = t22;
    $[77] = t37;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== t17 || $[80] !== t38) {
    t39 = <div className="mx-auto w-full max-w-[1440px] px-4 pt-2.5 pb-4 min-[744px]:py-4 md:px-8">{t17}{t38}</div>;
    $[79] = t17;
    $[80] = t38;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== categories || $[83] !== hoveredDept) {
    t40 = hoveredDept && <div className="absolute inset-x-0 top-full hidden bg-white shadow-[0_1px_4px_rgba(12,12,13,0.05),0_1px_4px_rgba(12,12,13,0.1)] lg:block" onMouseEnter={() => window.clearTimeout(closeTimer.current)} onMouseLeave={closeMegaSoon}><DepartmentMega department={hoveredDept} categories={categories} onNavigate={() => setHoveredDept(null)} /></div>;
    $[82] = categories;
    $[83] = hoveredDept;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== t10 || $[86] !== t39 || $[87] !== t40 || $[88] !== t8) {
    t41 = <header ref={ref} style={t8} className={t10}>{t39}{t40}</header>;
    $[85] = t10;
    $[86] = t39;
    $[87] = t40;
    $[88] = t8;
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  return t41;
}
function _temp5(s_1) {
  return s_1.ids.length;
}
function _temp4(s_0) {
  return s_0.orderIds.length;
}
function _temp3(s) {
  return s.cart?.itemCount ?? 0;
}